import { Skeleton } from "@mui/material";
import { useSettings } from "@sinch/core/providers/AppSettings";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import React, { ForwardedRef, forwardRef, useMemo } from "react";
import { LanguagesList } from "@sinch/components/application/LanguageSelectTextInput/queries.graphql";
import { LanguagesListQuery } from "@sinch/components/application/LanguageSelectTextInput/queries.types";
import {
  MultilanguageTextInput,
  MultilanguageTextInputProps,
} from "@sinch/components/form/MultilanguageTextInput/MultilanguageTextInput";

/**
 * Props for LanguageSelectTextInput component.
 * Omits the languages prop since it will be fetched from the server.
 */
export type LanguageSelectTextInputProps = Omit<MultilanguageTextInputProps, "languages">;

/**
 * MultilanguageTextInput component with server-fetched languages.
 *
 * This component wraps the MultilanguageTextInput component and automatically
 * fetches the available languages from the server using a GraphQL query.
 * Displays a skeleton loader while languages are being fetched.
 *
 * @param props - All MultilanguageTextInput props except languages
 * @param ref
 * @returns MultilanguageTextInput with server-fetched languages or skeleton during loading
 *
 * @example
 * ```tsx
 * <LanguageSelectTextInput
 *   label="Description"
 *   value={description}
 *   onChange={setDescription}
 * />
 * ```
 */
const LanguageSelectTextInputBase = (
  props: LanguageSelectTextInputProps,
  ref: ForwardedRef<HTMLDivElement | undefined>
) => {
  const { data, loading } = useQueryHandler<LanguagesListQuery>(LanguagesList);
  const { agencyLanguage, locale } = useSettings();

  const languages = useMemo(
    () =>
      (data?.languages ?? [])
        .map((lang) => ({
          language: lang.abbr,
          label: lang.abbr,
          title: getDisplayName(locale, lang.abbr),
        }))
        .sort((a, b) => {
          const aIsActive = normalize(a.language) === agencyLanguage;
          const bIsActive = normalize(b.language) === agencyLanguage;
          if (aIsActive !== bIsActive) return aIsActive ? -1 : 1;
          return a.language.localeCompare(b.language);
        }),
    [data?.languages, agencyLanguage, locale]
  );

  if (loading) {
    return <Skeleton height={56} variant="rectangular" />;
  }

  return <MultilanguageTextInput ref={ref} {...props} languages={languages} />;
};

export const LanguageSelectTextInput = forwardRef(LanguageSelectTextInputBase) as (
  props: LanguageSelectTextInputProps
) => ReturnType<typeof LanguageSelectTextInputBase>;

/**
 * Normalizes a language abbreviation to a valid BCP 47-like tag
 * by replacing non-alphanumeric characters with hyphens (e.g. "cs_CZ" → "cs-CZ").
 */
const normalize = (abbr: string) => abbr.replace(/[^a-zA-Z0-9]/g, "-");

/**
 * Resolves a human-readable display name for a language abbreviation
 * using the browser's `Intl.DisplayNames` API.
 *
 * Falls back gracefully for non-standard abbreviations (e.g. "cs_vol"):
 * splits on the separator, resolves the base language, and appends the variant
 * in parentheses — e.g. "Czech (vol)".
 *
 * @returns The localized display name, or `undefined` if the abbreviation is unresolvable.
 */
const getDisplayName = (locale: string, abbr: string): string | undefined => {
  const languageNames = new Intl.DisplayNames([locale], {
    type: "language",
    fallback: "none",
  });
  try {
    return languageNames.of(normalize(abbr));
  } catch {
    // Intl.DisplayNames throws RangeError for non-standard tags —
    // attempt to resolve the base language and append the variant suffix
    const [base, variant] = abbr.split(/[^a-zA-Z0-9]/, 2);
    if (!base.match(/[a-zA-Z]{2,3}/)) return undefined;
    return `${languageNames.of(base)} (${variant})`;
  }
};
